import {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useLocation, useNavigate} from "react-router-dom";
import classes from "./CreateEditMedicationTemplate.module.scss";
import {EDIT} from "../../../utils/constants";
import {FullPageContainer} from "../../common/FullPageContainer/FullPageContainer";
import {Modal} from "../../common/Modal/Modal";
import {CreateEditMedicationTemplateControls} from "./components/CreateEditMedicationTemplateControls/CreateEditMedicationTemplateControls";
import {useFormik} from "formik";
import {CreateEditMedicationTemplateDetails} from "./components/CreateEditMedicationTemplateDetails/CreateEditMedicationTemplateDetails";
import {CreateEditMedicationTemplateTimeline} from "./components/CreateEditMedicationTemplateTimeline/CreateEditMedicationTemplateTimeline";
import {
  CREATE_MEDICATION_TEMPLATE_REQUEST,
  GET_MEDICATION_TEMPLATE_REQUEST,
  UPDATE_MEDICATION_TEMPLATE_REQUEST,
} from "../../../redux/actions";
import {
  initialMedicationTemplateValues,
  validationSchemaMedicationTemplate,
} from "../../../utils/inputUtils";
import {
  currentMedicationTemplateSelector,
} from "../../../redux/selectors/treatmentPlansSelector";
import {ICreateEditView, IMedicationForTemplate} from "../../../utils/interfaces";

export const CreateEditMedicationTemplate = ({mode}: ICreateEditView) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);

  const [medications, setMedications] = useState<IMedicationForTemplate[]>([]);

  const id = location.pathname.split("/").pop() || "";
  const templateId = parseInt(id, 10);

  const currentMedicationPlan = useSelector(currentMedicationTemplateSelector);

  useEffect(() => {
    if (mode === EDIT) {
      dispatch({
        type: GET_MEDICATION_TEMPLATE_REQUEST,
        payload: {id}
      });
    }
  }, [dispatch, id, location.pathname, mode]);

  useEffect(() => {
    if (currentMedicationPlan && templateId) {
      setMedications(currentMedicationPlan.items);
    }
  }, [currentMedicationPlan, templateId]);

  const initialValues =
    mode === EDIT && currentMedicationPlan
      ? {
        id: currentMedicationPlan.id,
        name: currentMedicationPlan.name,
      }
      : initialMedicationTemplateValues;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: validationSchemaMedicationTemplate,
    onSubmit: values => {
      const data = {
        name: values.name,
        items: medications,
      };

      submitForm(data);
    }
  });

  const goToMedicationTemplates = () => {
    navigate("/medication-templates");
  }

  const goToMedicationTemplateView = () => {
    navigate(`/medication-templates/view/${id}`);
  }

  const closePopup = () => {
    setOpen(false);
  };

  const handleBack = () => {
    if (mode === EDIT) {
      goToMedicationTemplates();
      return;
    }
    handleBackAndCancelForCreate();
  };

  const handleCancel = () => {
    if (mode === EDIT) {
      goToMedicationTemplateView();
      return;
    }
    handleBackAndCancelForCreate();
  };

  const handleBackAndCancelForCreate = () => {
    if (initialValues.name === formik.values.name) {
      goToMedicationTemplates();
    } else {
      setOpen(true);
    }
  };

  const submitForm = (values: any) => {
    if (mode === EDIT) {
      dispatch({
        type: UPDATE_MEDICATION_TEMPLATE_REQUEST,
        payload: {data: {...values, items: values.items.map((item: any) => ({...item, dosageUnit: item.dosageUnit?.toUpperCase()})), id}, callback: () => { }, navigate, isEdit: true}
      });
    } else {
      dispatch({
        type: CREATE_MEDICATION_TEMPLATE_REQUEST,
        payload: {data: values, callback: () => { }, navigate, isEdit: false}
      });
    }
  };

  return (
    <FullPageContainer pageClassName={classes.createEditTemplateContainer}>
      <>
        <CreateEditMedicationTemplateControls
          navigateBack={handleBack}
          handleCancel={handleCancel}
          handleSave={formik.handleSubmit}
          submitDisabled={formik.values.name.trim() === "" || !!formik.errors.name || !medications.length}
        />
        <CreateEditMedicationTemplateDetails
          formik={formik}
        />
        <CreateEditMedicationTemplateTimeline
          medications={medications}
          setMedications={setMedications}
        />
        <Modal
          open={open}
          title={"Cancel changes"}
          message="Are you sure that you want to cancel creating the template? The data you've entered won't be saved."
          handleCancel={closePopup}
          handleConfirm={goToMedicationTemplates}
        />
      </>
    </FullPageContainer>
  );
};
